import { useCallback, useState } from "react";
import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import { AISetupModal } from "../../components/ai/AISetupModal";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { PreferenceCard, AcceptanceChart, ResetConfirmation } from "../../components/voiceProfile";
import { useAIConfig } from "../../contexts/AIConfigContext";
import { voiceProfileAPI } from "../../lib/api";
import type { VoiceProfilePageData } from "../../routes/loaders";
import styles from "./VoiceProfilePage.module.css";

function formatTone(tone: string | null): string | null {
  if (!tone) return null;
  return tone.charAt(0).toUpperCase() + tone.slice(1);
}

function formatComplexity(complexity: string | null): string | null {
  if (!complexity) return null;
  return complexity.charAt(0).toUpperCase() + complexity.slice(1);
}

function formatVocabulary(level: string | null): string | null {
  if (!level) return null;
  return level.charAt(0).toUpperCase() + level.slice(1);
}

function formatProfileAge(age: string): string {
  const match = age.match(/(\d+)\s*(day|week|month|year)s?/i);
  if (!match) return age;

  const [, num, unit] = match;
  return `${num} ${unit}${Number(num) !== 1 ? "s" : ""}`;
}

export default function VoiceProfilePage() {
  const { summary, analytics, error } = useLoaderData() as VoiceProfilePageData;
  const { t } = useTranslation();
  const { aiConfigured } = useAIConfig();
  const revalidator = useRevalidator();
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showAISetup, setShowAISetup] = useState(false);
  const handleAIConfigured = useCallback(() => {
    setShowAISetup(false);
    revalidator.revalidate();
  }, [revalidator]);

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await voiceProfileAPI.reset();
      toast.success(t("voiceProfile.resetSuccess"));
      setShowResetModal(false);
      revalidator.revalidate();
    } catch {
      toast.error(t("voiceProfile.resetError"));
    } finally {
      setIsResetting(false);
    }
  };

  const hasProfile = summary !== null;
  const hasData = hasProfile && summary.totalSuggestionsProcessed > 0;

  return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>{t("voiceProfile.title")}</h1>
            <p className={styles.subtitle}>{t("voiceProfile.subtitle")}</p>
          </div>
          <div className={styles.headerActions}>
            {summary?.profileAge && (
              <span className={styles.profileAge}>
                {t("voiceProfile.profileAge", { age: formatProfileAge(summary.profileAge) })}
              </span>
            )}
            {hasData && (
              <button
                type="button"
                className={styles.resetButton}
                onClick={() => setShowResetModal(true)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                {t("voiceProfile.resetButton")}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className={styles.errorState}>
            <p className={styles.errorText}>{error}</p>
          </div>
        )}

        {!error && !hasData && (
          <div className={styles.emptyStateWrapper}>
            <div className={styles.emptyBanner}>
              <div className={styles.emptyIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </div>
              <div className={styles.emptyBannerText}>
                <h2 className={styles.emptyTitle}>
                  {aiConfigured === false
                    ? t("voiceProfile.aiRequiredTitle")
                    : t("voiceProfile.emptyTitle")}
                </h2>
                <p className={styles.emptyText}>
                  {aiConfigured === false
                    ? t("voiceProfile.aiRequiredDescription")
                    : t("voiceProfile.emptyDescription")}
                </p>
                {aiConfigured === null ? null : aiConfigured === false ? (
                  <button
                    type="button"
                    className={styles.startWritingButton}
                    onClick={() => setShowAISetup(true)}
                  >
                    {t("voiceProfile.connectAI")}
                  </button>
                ) : (
                  <Link to="/dashboard" className={styles.startWritingButton}>
                    {t("voiceProfile.startWriting")}
                  </Link>
                )}
              </div>
            </div>

            {/* Ghost preview: stats */}
            <div className={`${styles.statsGrid} ${styles.ghost}`}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>—</div>
                <div className={styles.statLabel}>{t("voiceProfile.suggestionsProcessed")}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>—</div>
                <div className={styles.statLabel}>{t("voiceProfile.acceptanceRate")}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>—</div>
                <div className={styles.statLabel}>{t("voiceProfile.mostAccepted")}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>—</div>
                <div className={styles.statLabel}>{t("voiceProfile.leastAccepted")}</div>
              </div>
            </div>

            {/* Ghost preview: preference cards */}
            <div className={`${styles.content} ${styles.ghost}`}>
              <h2 className={styles.sectionTitle}>{t("voiceProfile.startingBaseline")}</h2>

              <PreferenceCard
                label={t("voiceProfile.tone")}
                value={null}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                }
                description={t("voiceProfile.toneDescription")}
                emptyText={t("voiceProfile.buildsAsYouWrite")}
              />

              <PreferenceCard
                label={t("voiceProfile.sentenceComplexity")}
                value={null}
                variant="meter"
                meterLabels={[
                  t("voiceProfile.meterSimple"),
                  t("voiceProfile.meterModerate"),
                  t("voiceProfile.meterComplex"),
                ]}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="4" y1="9" x2="20" y2="9" />
                    <line x1="4" y1="15" x2="20" y2="15" />
                    <line x1="10" y1="3" x2="8" y2="21" />
                    <line x1="16" y1="3" x2="14" y2="21" />
                  </svg>
                }
                emptyText={t("voiceProfile.buildsAsYouWrite")}
              />

              <PreferenceCard
                label={t("voiceProfile.vocabularyLevel")}
                value={null}
                variant="meter"
                meterLabels={[
                  t("voiceProfile.meterBasic"),
                  t("voiceProfile.meterIntermediate"),
                  t("voiceProfile.meterAdvanced"),
                ]}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                }
                emptyText={t("voiceProfile.buildsAsYouWrite")}
              />

              <PreferenceCard
                label={t("voiceProfile.literaryDevices")}
                value={null}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                }
                description={t("voiceProfile.literaryDevicesDescription")}
                emptyText={t("voiceProfile.buildsAsYouWrite")}
              />
            </div>
          </div>
        )}

        {!error && hasData && summary && analytics && (
          <>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{summary.totalSuggestionsProcessed}</div>
                <div className={styles.statLabel}>{t("voiceProfile.suggestionsProcessed")}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {Math.round(analytics.overallAcceptanceRate * 100)}%
                </div>
                <div className={styles.statLabel}>{t("voiceProfile.acceptanceRate")}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {analytics.mostAcceptedCategory
                    ? analytics.mostAcceptedCategory.charAt(0).toUpperCase() +
                      analytics.mostAcceptedCategory.slice(1)
                    : "-"}
                </div>
                <div className={styles.statLabel}>{t("voiceProfile.mostAccepted")}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {analytics.leastAcceptedCategory
                    ? analytics.leastAcceptedCategory.charAt(0).toUpperCase() +
                      analytics.leastAcceptedCategory.slice(1)
                    : "-"}
                </div>
                <div className={styles.statLabel}>{t("voiceProfile.leastAccepted")}</div>
              </div>
            </div>

            <div className={styles.content}>
              <h2 className={styles.sectionTitle}>{t("voiceProfile.learnedPreferences")}</h2>

              <PreferenceCard
                label={t("voiceProfile.tone")}
                value={formatTone(summary.tone)}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                }
                description={t("voiceProfile.toneDescription")}
                emptyText={t("voiceProfile.notEnoughData")}
              />

              <PreferenceCard
                label={t("voiceProfile.sentenceComplexity")}
                value={formatComplexity(summary.sentenceComplexity)}
                variant="meter"
                meterLevel={
                  summary.sentenceComplexity as "simple" | "moderate" | "complex" | undefined
                }
                meterLabels={[
                  t("voiceProfile.meterSimple"),
                  t("voiceProfile.meterModerate"),
                  t("voiceProfile.meterComplex"),
                ]}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="4" y1="9" x2="20" y2="9" />
                    <line x1="4" y1="15" x2="20" y2="15" />
                    <line x1="10" y1="3" x2="8" y2="21" />
                    <line x1="16" y1="3" x2="14" y2="21" />
                  </svg>
                }
                emptyText={t("voiceProfile.notEnoughData")}
              />

              <PreferenceCard
                label={t("voiceProfile.vocabularyLevel")}
                value={formatVocabulary(summary.vocabularyLevel)}
                variant="meter"
                meterLevel={
                  summary.vocabularyLevel as "basic" | "intermediate" | "advanced" | undefined
                }
                meterLabels={[
                  t("voiceProfile.meterBasic"),
                  t("voiceProfile.meterIntermediate"),
                  t("voiceProfile.meterAdvanced"),
                ]}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                }
                emptyText={t("voiceProfile.notEnoughData")}
              />

              <PreferenceCard
                label={t("voiceProfile.literaryDevices")}
                value={
                  summary.literaryDevices.length > 0
                    ? t("voiceProfile.preferred", { count: summary.literaryDevices.length })
                    : null
                }
                tags={summary.literaryDevices}
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                }
                description={t("voiceProfile.literaryDevicesDescription")}
                emptyText={t("voiceProfile.noPreferredDevices")}
              />

              <div className={styles.chartSection}>
                <AcceptanceChart
                  acceptanceRates={analytics.acceptanceRatesByCategory}
                  overallRate={analytics.overallAcceptanceRate}
                  totalSuggestions={analytics.totalSuggestions}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {showResetModal && (
        <ResetConfirmation
          onConfirm={handleReset}
          onCancel={() => setShowResetModal(false)}
          isResetting={isResetting}
        />
      )}
      {showAISetup && (
        <AISetupModal onClose={() => setShowAISetup(false)} onConfigured={handleAIConfigured} />
      )}
    </>
  );
}
